import React, { useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Spinner } from "../../components";
import { Button as Btn, Typography } from "@material-ui/core";
import { DarkGrey, PrimaryColor } from "../../shared/styles/colors";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "../../assets/admin/edit-icon.svg";
import Permission from "../../model/permission";
import Role from "../../model/role";
import DeleteModal from "../modal/DeleteModal";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ApiService from "../../service/ApiService";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import AlertService from "../../service/AlertService";
import ToastMessages from "../../model/enum/ToastMessages";
import RolePermission from "../../model/rolePermission";
import useStyles from "./styles";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "35rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

const RoleModal = ({ role, permissions, rolePermissions, resetList }: any) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const edit = role != null;
  const id = edit ? role.id : 0;
  const [name, setName] = useState(edit ? role.name : "");
  const [loading, setLoading] = useState(false);
  const [togglePermissions, setTogglePermissions] = useState(true);
  const [newPermissions, setNewPermissions] = useState<number[]>(
    edit
      ? Array.isArray(rolePermissions) && edit
        ? rolePermissions.map((rp: RolePermission) => rp.permissionId)
        : []
      : []
  );

  const handleName = (e: any) => setName(e.target.value);
  const handleTogglePermissions = () =>
    setTogglePermissions((togglePermissions) => !togglePermissions);

  const handlePermissions = (permission: Permission, active: boolean) => {
    const permissionId = permission.id;
    if (active) {
      setNewPermissions([...newPermissions, permissionId]);
    } else {
      setNewPermissions(newPermissions.filter((id) => id !== permissionId));
    }
  };

  const handleCreate = async (payload: Role) => {
    return await ApiService.post<Role>(DatabaseEntities.ROLES, payload)
      .then((res: Role) => {
        AlertService.success(ToastMessages.CREATED);
        return res;
      })
      .catch(() => {
        AlertService.error(ToastMessages.CREATE_FAILED);
        return undefined;
      });
  };

  const handleUpdate = async (payload: Role) => {
    await ApiService.put<Role>(
      `${DatabaseEntities.ROLES}/${payload.id}`,
      payload
    )
      .then((res: Role) => {
        AlertService.success(ToastMessages.UPDATED);
        return res;
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: Role) => {
    await ApiService.delete<any>(`${DatabaseEntities.ROLES}/${payload.id}`)
      .then(() => {
        AlertService.success(ToastMessages.DELETED);
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const handleAssingPermissions = async (
    roleId: number,
    permissionIds: number[]
  ) => {
    await ApiService.post(
      `${DatabaseEntities.ROLES}/${id}/${DatabaseEntities.PERMISSIONS}`,
      {
        roleId: roleId,
        permissionIds: permissionIds,
      }
    ).catch(() => {
      AlertService.error(ToastMessages.PERMISSIONS_UPDATE_FAILED);
    });
  };

  const register = async () => {
    if (invalidInput()) return;

    setLoading(true);
    const payload: Role = {
      id: id,
      name: name,
    };

    if (edit) {
      await handleUpdate(payload).then(() => {
        if (newPermissions.length > 0) {
          handleAssingPermissions(id, newPermissions);
        }
      });
    } else {
      await handleCreate(payload).then((res) => {
        if (res != null && res.id && newPermissions.length > 0) {
          handleAssingPermissions(res.id, newPermissions);
        }
      });

      setLoading(false);
      resetList();
      handleClose();
    }

    setName("");
    setLoading(false);
    setTogglePermissions(false);
    handleClose();
  };

  const invalidInput = () => {
    return name === "";
  };

  return (
    <div>
      {edit ? (
        <Button onClick={handleOpen}>
          <img src={EditIcon} alt="edit icon" />
        </Button>
      ) : (
        <Btn
          className={classes.headerButton}
          variant="contained"
          onClick={handleOpen}
        >
          + Add Role
        </Btn>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Box sx={style}>
            <div className={classes.headerDiv}>
              {edit ? (
                <Typography className={classes.title}>
                  <DeleteModal item={role} handleDelete={handleDelete} />
                </Typography>
              ) : (
                <Typography className={classes.title}>Add role</Typography>
              )}
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
            <hr />
            <div className={classes.form}>
              <input
                className={classes.input}
                type="text"
                value={name}
                onChange={handleName}
                placeholder="Role name"
              />
              <div className={classes.permissionDiv}>
                <Typography className={classes.subTitle}>
                  Permissions
                </Typography>
                {togglePermissions ? (
                  <ExpandLessIcon onClick={handleTogglePermissions} />
                ) : (
                  <ExpandMoreIcon onClick={handleTogglePermissions} />
                )}
              </div>
              {togglePermissions && (
                <>
                  <hr />
                  <div className={classes.permissionsList}>
                    {permissions.map((perm: Permission) => (
                      <div className={classes.permissionItem} key={perm.id}>
                        <Typography>{perm.name}</Typography>
                        {newPermissions?.includes(perm.id) ? (
                          <Button
                            sx={{ color: PrimaryColor }}
                            onClick={() => handlePermissions(perm, false)}
                          >
                            <ToggleOnIcon />
                          </Button>
                        ) : (
                          <Button
                            sx={{ color: DarkGrey }}
                            onClick={() => handlePermissions(perm, true)}
                          >
                            <ToggleOffIcon />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className={classes.buttonDiv}>
              <Btn
                className={classes.button}
                onClick={register}
                disabled={invalidInput()}
              >
                {edit ? "Save" : "Create"}
              </Btn>
            </div>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default RoleModal;
